import React, { useState, useMemo } from 'react'

import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableFooter from '@mui/material/TableFooter'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import SettingsSection from './SettingsSection'
import formatCurrency, { sumKronor } from './Formatting'
import { apiGetBudget, apiSetBudget } from './Api'
import { useApiData, LoadError, Loading } from './useApiData'

// The budget is a result: income minus expenses. Most sections plan a deficit — they spend what
// the club allocates them — so the field has to take a minus sign, which rules out CurrencyInput
// (it masks to a non-negative amount). A plain field parsed here is enough.
function parseAmount(text: string): number | null | 'invalid' {
    const trimmed = text.trim().replace(/\s/g, '').replace(',', '.')
    if (trimmed === '' || trimmed === '-') {
        return null                      // an empty field means no budget, not a budget of zero
    }
    const value = Number(trimmed)
    return Number.isFinite(value) ? value : 'invalid'
}

function BudgetRow({ id, label, name, amount, onSave }: {
    id: string,
    label: string,
    name: string,
    amount: number | undefined,
    onSave: (amount: number | null) => void,
}) {
    const [text, setText] = useState(amount === undefined ? '' : String(amount))
    const parsed = parseAmount(text)
    const invalid = parsed === 'invalid'

    // Saved on blur rather than per keystroke: a half-typed "-4" is a different budget from "-40 000",
    // and every keystroke would be one.
    const commit = () => {
        if (invalid) return
        const current = amount ?? null
        if (parsed !== current) onSave(parsed)
    }

    return (
        <TableRow>
            <TableCell>
                {label}
                <Typography variant="body2" color="text.secondary">{name}</Typography>
            </TableCell>
            <TableCell align="right">
                <TextField
                    // Stable per row, so the field can be reached by selector when the app is
                    // driven (.claude/skills/run-eko) rather than only by eye.
                    id={id}
                    size="small"
                    value={text}
                    error={invalid}
                    helperText={invalid ? 'Ogiltigt belopp' : undefined}
                    onChange={event => setText(event.target.value)}
                    onBlur={commit}
                    slotProps={{ htmlInput: { inputMode: 'numeric', style: { textAlign: 'right' } } }}
                    sx={{ width: '12rem' }}
                />
            </TableCell>
        </TableRow>
    )
}

export default function BudgetTab() {
    const { data, loading, error, setData, reload } = useApiData(apiGetBudget, [])
    const [year, setYear] = useState<string | null>(null)

    // Defaults to the year running now. A budget is decided before the year it governs, so the
    // picker must offer the coming one too — it just isn't where you land.
    const selectedYear = year ?? data?.fiscal_years.find(y => y.is_current)?.start
        ?? data?.fiscal_years.at(-1)?.start ?? null

    const amounts = useMemo(
        () => (selectedYear === null ? {} : data?.budgets[selectedYear] ?? {}),
        [data, selectedYear])

    // Written straight through rather than via the call chain: this is a blur on one field, and
    // the chain's full-screen backdrop would flash on every tab away from an input. Failures still
    // reach the snackbar — parseResponse reports every non-OK response to the error bus.
    const saveAmount = async (costCenterItemId: string, amount: number | null) => {
        if (selectedYear === null) return
        await apiSetBudget(selectedYear, costCenterItemId, amount)
        setData(draft => {
            if (draft === null) return
            const forYear = draft.budgets[selectedYear] ?? (draft.budgets[selectedYear] = {})
            if (amount === null) delete forYear[costCenterItemId]
            else forYear[costCenterItemId] = amount
        })
    }

    if (error !== null) return <LoadError onRetry={reload}/>
    if (data === null || loading) return <Loading/>

    const total = sumKronor(Object.values(amounts))
    const budgeted = Object.keys(amounts).length

    return (
        <SettingsSection
            title="Budget"
            description={'Förväntat resultat per kostnadsställe: intäkter minus kostnader. Ett minustecken '
                + 'betyder planerat underskott. Tomt fält betyder att ingen budget är satt, vilket '
                + 'inte är samma sak som en budget på noll.'}
        >
            <TextField
                select
                size="small"
                label="Verksamhetsår"
                value={selectedYear ?? ''}
                onChange={event => setYear(event.target.value)}
                sx={{ mb: 2, width: '14rem' }}
            >
                {data.fiscal_years.map(fiscalYear => (
                    <MenuItem key={fiscalYear.start} value={fiscalYear.start}>
                        {fiscalYear.label}{fiscalYear.is_current ? ' (pågående)' : ''}
                    </MenuItem>
                ))}
            </TextField>

            {data.fiscal_years.length === 0
                ? <Typography>Visma har inga verksamhetsår för den här föreningen.</Typography>
                : <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Kostnadsställe</TableCell>
                            <TableCell align="right">Budgeterat resultat</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.cost_center_items.map(item => (
                            <BudgetRow
                                // Keyed on the year too, so switching year re-mounts the fields
                                // with that year's values instead of keeping the old text.
                                key={`${selectedYear}:${item.cost_center_item_id}`}
                                id={`budget-${item.short_name}`}
                                label={item.short_name}
                                name={item.name}
                                amount={amounts[item.cost_center_item_id]}
                                onSave={amount => { void saveAmount(item.cost_center_item_id, amount) }}
                            />
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            {/* The sum is the point of editing them together: it is the club's own
                                expected result, and the number the board approved. */}
                            <TableCell sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                Summa
                                <Typography variant="body2" color="text.secondary">
                                    {budgeted} av {data.cost_center_items.length} kostnadsställen budgeterade
                                </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                {formatCurrency(total)} kr
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>}
        </SettingsSection>
    )
}
